function b64url(buf) {
    return btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function randomString(len = 64) {
    const a = new Uint8Array(len);
    crypto.getRandomValues(a);
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    return Array.from(a, (x) => alphabet[x % alphabet.length]).join('');
}
export function decodeJwt(token) {
    try {
        const [, payload] = token.split('.');
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decodeURIComponent(Array.from(json, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')));
    }
    catch {
        return null;
    }
}
/** Code → in-flight exchange, shared by every `OsAuth` instance in this page (see `completeLogin`). */
const exchanges = new Map();
export function createOsAuth(o) {
    const options = { issuer: o.issuer.replace(/\/$/, ''), clientId: o.clientId, redirectPath: o.redirectPath ?? '/auth/callback', scope: o.scope ?? 'openid profile email offline_access', devBypass: o.devBypass ?? false };
    const prefix = o.storagePrefix ?? `os.${o.clientId}`;
    const K = { at: `${prefix}.at`, rt: `${prefix}.rt`, it: `${prefix}.it`, pkce: `${prefix}.pkce` };
    const store = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    let accessToken = store?.getItem(K.at) ?? null;
    let refreshToken = store?.getItem(K.rt) ?? null;
    /** Kept only to send as `id_token_hint` on logout so the OS can skip its sign-out confirmation. */
    let idToken = store?.getItem(K.it) ?? null;
    /** Set for the rest of the page's life once logout starts; nothing may begin a new login after that. */
    let loggingOut = false;
    const setIdToken = (it) => { idToken = it; if (it)
        store?.setItem(K.it, it);
    else
        store?.removeItem(K.it); };
    const listeners = new Set();
    /** In-flight refresh shared by concurrent callers (see refreshSession). */
    let refreshing = null;
    const notify = () => listeners.forEach((l) => l());
    const redirectUri = () => `${window.location.origin}${options.redirectPath}`;
    function setTokens(at, rt) {
        accessToken = at;
        refreshToken = rt;
        if (at)
            store?.setItem(K.at, at);
        else
            store?.removeItem(K.at);
        if (rt)
            store?.setItem(K.rt, rt);
        else
            store?.removeItem(K.rt);
        notify();
    }
    const auth = {
        options,
        getAccessToken: () => accessToken,
        isDevBypass: () => options.devBypass,
        isAuthenticated() {
            if (options.devBypass)
                return true;
            if (!accessToken)
                return false;
            const exp = decodeJwt(accessToken)?.exp;
            return !exp || exp * 1000 > Date.now() - 30_000 || !!refreshToken;
        },
        getClaims: () => (accessToken ? decodeJwt(accessToken) : null),
        getRoles: () => auth.getClaims()?.apps?.find((a) => a.id === options.clientId)?.roles ?? [],
        async beginLogin(returnTo = window.location.pathname + window.location.search) {
            // A logout in flight must not be overtaken by a new authorization request.
            if (loggingOut)
                return;
            const verifier = randomString(96);
            const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
            const state = randomString(24);
            store?.setItem(K.pkce, JSON.stringify({ verifier, state, returnTo }));
            const u = new URL(`${options.issuer}/authorize`);
            u.search = new URLSearchParams({ client_id: options.clientId, redirect_uri: redirectUri(), response_type: 'code', scope: options.scope, state, code_challenge: challenge, code_challenge_method: 'S256' }).toString();
            window.location.assign(u.toString());
        },
        completeLogin(params) {
            const code = params.get('code');
            if (params.get('error'))
                return Promise.reject(new Error(params.get('error_description') ?? params.get('error')));
            // Authorization codes are single-use. React StrictMode (and re-mounted callback routes) call this twice for the
            // same URL; the second call must join the first exchange instead of redeeming the code again.
            const inflight = code ? exchanges.get(code) : undefined;
            if (inflight)
                return inflight;
            const run = (async () => {
                const saved = JSON.parse(store?.getItem(K.pkce) ?? 'null');
                if (!saved || !code || params.get('state') !== saved.state)
                    throw new Error('Login state mismatch; start again.');
                const res = await fetch(`${options.issuer}/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ grant_type: 'authorization_code', code, redirect_uri: redirectUri(), client_id: options.clientId, code_verifier: saved.verifier }) });
                if (!res.ok)
                    throw new Error('Token exchange failed');
                const tok = (await res.json());
                setIdToken(tok.id_token ?? null);
                setTokens(tok.access_token, tok.refresh_token ?? null);
                store?.removeItem(K.pkce);
                return saved.returnTo || '/';
            })();
            if (code)
                exchanges.set(code, run);
            return run;
        },
        refreshSession() {
            if (options.devBypass || !refreshToken)
                return Promise.resolve(false);
            // Refresh tokens rotate: the OS revokes the old one as it issues the new one. Several requests failing with 401 at
            // the same moment (a page load after the access token expired) must share ONE refresh — a second POST with the
            // already-rotated token would come back invalid_grant and wipe the fresh tokens the first call just stored.
            if (refreshing)
                return refreshing;
            const used = refreshToken;
            refreshing = (async () => {
                try {
                    const res = await fetch(`${options.issuer}/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ grant_type: 'refresh_token', refresh_token: used, client_id: options.clientId }) });
                    if (!res.ok) {
                        // Only drop the session if nothing else replaced the token meanwhile; a transient server error keeps it.
                        if (res.status === 400 && refreshToken === used)
                            setTokens(null, null);
                        return false;
                    }
                    const tok = (await res.json());
                    setTokens(tok.access_token, tok.refresh_token ?? used);
                    return true;
                }
                catch {
                    return false;
                }
                finally {
                    refreshing = null;
                }
            })();
            return refreshing;
        },
        isLoggingOut() { return loggingOut; },
        logout(redirectTo) {
            const hint = idToken;
            // Set BEFORE clearing tokens: clearing notifies subscribers, and a listener that re-renders AuthGuard would
            // otherwise start a login before this function reaches its own navigation.
            loggingOut = true;
            setIdToken(null);
            setTokens(null, null);
            const back = redirectTo ?? window.location.origin;
            if (options.devBypass) {
                window.location.assign(back);
                return;
            }
            // `id_token_hint` lets the OS end the session without its confirmation page (OIDC RP-Initiated Logout).
            const q = new URLSearchParams({ post_logout_redirect_uri: back });
            if (hint)
                q.set('id_token_hint', hint);
            window.location.assign(`${options.issuer}/logout?${q.toString()}`);
        },
        subscribe(l) { listeners.add(l); return () => listeners.delete(l); },
    };
    return auth;
}
/**
 * Typed fetch wrapper that attaches the bearer token and retries once after a refresh on 401.
 * Apps usually wrap this with their own zod parsing / error mapping.
 */
export async function authFetch(auth, input, init = {}, retry = true) {
    const headers = new Headers(init.headers);
    const token = auth.getAccessToken();
    if (token)
        headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(input, { ...init, headers });
    if (res.status === 401 && retry && (await auth.refreshSession()))
        return authFetch(auth, input, init, false);
    return res;
}
//# sourceMappingURL=index.js.map